'use client';

import type { CSSProperties, ComponentProps } from 'react';
import type { BaseReactPlayerProps } from 'react-player/base';
import ReactPlayer from 'react-player/file';
import { useHasMounted } from '@frameio/vapor';
import { VideoAsset } from '@/sanity/schemas/video-asset/types';
import { SanityImage } from '@/components/SanityImage';

type VideoPlayerProps = {
  url: VideoAsset['videoFile'];
  /**
   * @description the thumbnail sanity image for the video, if not available, the video will be embedded regardless
   */
  thumbnail?: VideoAsset['thumbnail'];
  /**
   * @description alt text for the thumbnail image
   */
  alt?: string;
  style?: CSSProperties;
  thumbnailProps?: Partial<ComponentProps<typeof SanityImage>>;
};

export function VideoPlayer({
  url,
  thumbnail,
  thumbnailProps,
  alt = ``,
  style,
}: VideoPlayerProps) {
  const hasMounted = useHasMounted();

  // create a thumbnail to show before the video loads
  // if one is available and the component has mounted
  const lightThumbnail: BaseReactPlayerProps['light'] = thumbnail &&
    hasMounted && <SanityImage alt={alt} src={thumbnail} {...thumbnailProps} />;

  return url && hasMounted ? (
    <ReactPlayer
      width="100%"
      height="100%"
      url={url}
      controls={true}
      light={lightThumbnail}
      config={{
        forceHLS: true,
        attributes: {
          forceHLS: true,
          hlsOptions: {
            testBandwidth: false,
            startLevel: 4,
            capLevelToPlayerSize: true,
          },
        },
      }}
      style={style}
    />
  ) : null;
}
